import { ref, computed } from "vue";
import { useAxios, useSession, useNotification, useDayJS } from ".";

const loading = ref(false);
const fetching = ref(false);

const tasks = ref({});
const task = ref({});
const taskCategory = ref("");
const taskTimeline = ref([]);

const fetchedTimelineKey = ref(null);

export default function useTask() {
  const axios = useAxios();
  const session = useSession();
  const dayjs = useDayJS();
  const { createNotification } = useNotification();

  const { active_year, active_quarter } = session.value;

  const getTasks = async (filters = {}) => {
    fetching.value = true;
    try {
      const res = await axios.get("/tasks", {
        params: {
          role_id: session.value.role.id,
          year: active_year,
          ...filters,
        },
      });
      tasks.value = res.data.data ?? {};
    } catch (err) {
      console.log(err);
    } finally {
      fetching.value = false;
    }
  };

  const getTask = async (id) => {
    fetching.value = true;
    try {
      const res = await axios.get(`/tasks/${id}`);
      task.value = res.data.data ?? {};
    } catch (err) {
      console.log(err);
    } finally {
      fetching.value = false;
    }
  };

  const storeTask = async (payload, callback) => {
    loading.value = true;
    try {
      await axios.post("/tasks", payload);
      if (callback) await callback();
    } catch (err) {
      console.log(err);
    } finally {
      loading.value = false;
    }
  };

  const updateTask = async (id, payload, callback, notify = false) => {
    try {
      const res = await axios.put(`/tasks/${id}`, {
        role_id: session.value.role.id,
        ...payload,
      });

      const data = res.data.data;
      if (callback) await callback(data);

      if (notify) {
        await createNotification(
          {
            task_category_id: data.task_category_id,
            category: data.task_category.slug,
            icon: "info",
            title: `${data.task_category.name} Telah di Publish`,
            content: `<p>Task <b>${data.task_category.name}</b> telah di publish oleh Admin.
                      Lihat timeline <a href="/task" class="text-primary font-bold">disini</a></p>`,
            path: "/task",
          },
          {
            title: `${data.task_category.name} Berhasil di Publish`,
            content: `<p>Task <b>${data.task_category.name}</b> berhasil di publish</p>`,
          },
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateTaskStatus = async (id, payload, callback) => {
    loading.value = true;
    try {
      await axios.patch(`/tasks/${id}`, payload);
      if (callback) await callback();
    } catch (err) {
      console.log(err);
    } finally {
      loading.value = false;
    }
  };

  const getTaskCategory = async (filters = {}) => {
    fetching.value = true;
    try {
      const res = await axios.get("/tasks/category", {
        params: {
          year: active_year,
          ...filters,
        },
      });
      taskCategory.value = res.data.data ?? "";
    } catch (err) {
      console.log(err);
    } finally {
      fetching.value = false;
    }
  };

  const getTaskTimeline = async (filters = {}) => {
    const year = filters.year ?? active_year;
    const quarter = filters.quarter ?? active_quarter;
    const key = `${year}-${quarter}`;

    // 🔒 CACHE GUARD
    if (fetchedTimelineKey.value === key && taskTimeline.value.length) {
      return;
    }

    fetchedTimelineKey.value = key;
    fetching.value = true;

    try {
      const res = await axios.get("/tasks/timeline", {
        params: { year, quarter },
      });

      const data = res.data.data;
      taskTimeline.value = [data.start_date ?? null, data.end_date ?? null];
    } catch (err) {
      console.log(err);
    } finally {
      fetching.value = false;
    }
  };
  const taskPeriod = computed(() => {
    if (!taskTimeline.value.length || taskTimeline.value.some((t) => t == null)) {
      return "Start Date belum ditetapkan - End Date belum ditetapkan";
    }

    const [start_date, end_date] = taskTimeline.value;

    const start =
      start_date && start_date !== "0001-01-01T00:00:00Z"
        ? dayjs(start_date).format("DD MMMM YYYY")
        : "Start Date belum ditetapkan";

    const end =
      end_date && end_date !== "0001-01-01T00:00:00Z"
        ? dayjs(end_date).format("DD MMMM YYYY")
        : "End Date belum ditetapkan";

    return `${start} - ${end}`;
  });
  return {
    loading,
    fetching,
    tasks,
    task,
    taskCategory,
    taskTimeline,
    taskPeriod,
    getTasks,
    getTask,
    storeTask,
    updateTask,
    updateTaskStatus,
    getTaskCategory,
    getTaskTimeline,
  };
}